import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
  type IAuthenticationCallback,
} from 'amazon-cognito-identity-js'
import { env } from './env'

// Login no pool admin do VSL Max (MFA obrigatório, só por convite).
// Primeiro acesso: senha temporária do convite → nova senha → cadastro do TOTP (QR) → código.
// Depois: senha → código do app autenticador.

export type Step =
  | { kind: 'done' }
  | { kind: 'newPassword' }
  | { kind: 'mfaSetup'; secret: string; otpauth: string }
  | { kind: 'totp' }

let pool: CognitoUserPool | null = null
let pending: CognitoUser | null = null
let pendingUsername = ''

function userPool(): CognitoUserPool {
  pool ??= new CognitoUserPool({ UserPoolId: env.userPoolId, ClientId: env.clientId })
  return pool
}

export const otpauthUri = (username: string, secret: string): string =>
  `otpauth://totp/${encodeURIComponent(`VSL Max Backoffice:${username}`)}?secret=${secret}&issuer=${encodeURIComponent('VSL Max Backoffice')}`

// Traduz as mensagens do Cognito que o usuário vê de verdade.
export function authErrorMessage(error: unknown): string {
  const e = error as { code?: string; name?: string; message?: string }
  const code = e?.code ?? e?.name ?? ''
  const map: Record<string, string> = {
    NotAuthorizedException: 'E-mail ou senha incorretos.',
    UserNotFoundException: 'E-mail ou senha incorretos.',
    CodeMismatchException: 'Código inválido. Confira o app autenticador e tente de novo.',
    EnableSoftwareTokenMFAException: 'Código inválido. Confira o app autenticador e tente de novo.',
    ExpiredCodeException: 'Código expirado. Use o código atual do app.',
    InvalidPasswordException: 'A senha precisa de 8 caracteres ou mais, com maiúscula, minúscula, número e símbolo.',
    LimitExceededException: 'Muitas tentativas. Espere alguns minutos.',
    TooManyRequestsException: 'Muitas tentativas. Espere alguns minutos.',
    PasswordResetRequiredException: 'Sua senha precisa ser redefinida. Peça um novo convite.',
  }
  if (code === 'NotAuthorizedException' && /expired/i.test(e?.message ?? '')) {
    return 'A sessão de login expirou. Comece de novo.'
  }
  return map[code] ?? e?.message ?? 'Não foi possível entrar.'
}

function callbacks(resolve: (step: Step) => void, reject: (error: unknown) => void): IAuthenticationCallback {
  return {
    onSuccess: () => resolve({ kind: 'done' }),
    onFailure: reject,
    newPasswordRequired: () => resolve({ kind: 'newPassword' }),
    mfaSetup: () => {
      pending?.associateSoftwareToken({
        associateSecretCode: (secret: string) =>
          resolve({ kind: 'mfaSetup', secret, otpauth: otpauthUri(pendingUsername, secret) }),
        onFailure: reject,
      })
    },
    totpRequired: () => resolve({ kind: 'totp' }),
  }
}

export function signIn(username: string, password: string): Promise<Step> {
  pendingUsername = username.trim()
  pending = new CognitoUser({ Username: pendingUsername, Pool: userPool() })
  pending.setAuthenticationFlowType('USER_SRP_AUTH')
  const details = new AuthenticationDetails({ Username: pendingUsername, Password: password })
  return new Promise((resolve, reject) => pending!.authenticateUser(details, callbacks(resolve, reject)))
}

export function completeNewPassword(newPassword: string): Promise<Step> {
  return new Promise((resolve, reject) => {
    if (!pending) return reject(new Error('Comece pelo login.'))
    pending.completeNewPasswordChallenge(newPassword, {}, callbacks(resolve, reject))
  })
}

export function verifyMfaSetup(code: string): Promise<Step> {
  return new Promise((resolve, reject) => {
    if (!pending) return reject(new Error('Comece pelo login.'))
    pending.verifySoftwareToken(code.trim(), 'VSL Max Backoffice', {
      onSuccess: () => resolve({ kind: 'done' }),
      onFailure: reject,
    })
  })
}

export function sendTotp(code: string): Promise<Step> {
  return new Promise((resolve, reject) => {
    if (!pending) return reject(new Error('Comece pelo login.'))
    pending.sendMFACode(code.trim(), callbacks(resolve, reject), 'SOFTWARE_TOKEN_MFA')
  })
}

// idToken válido (o SDK renova pelo refresh token quando vence). null = precisa logar.
export function currentIdToken(): Promise<string | null> {
  const user = userPool().getCurrentUser()
  if (!user) return Promise.resolve(null)
  return new Promise((resolve) => {
    user.getSession((error: Error | null, session: CognitoUserSession | null) => {
      if (error || !session?.isValid()) return resolve(null)
      resolve(session.getIdToken().getJwtToken())
    })
  })
}

export function currentEmail(): string | null {
  const user = userPool().getCurrentUser()
  return user?.getUsername() ?? null
}

export function signOut(): void {
  userPool().getCurrentUser()?.signOut()
  pending = null
}
